import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  FileText,
  Scale,
  CheckCircle2,
  ArrowRight,
  ClipboardCheck,
  Info,
  Landmark,
  Shield,
  Camera,
  Keyboard,
  FileSearch,
  Wrench,
  Car,
  Heart,
  Settings,
  FileWarning,
  Receipt,
  AlertTriangle,
  TrendingDown,
  CircleMinus,
  Sparkles,
  XCircle,
  Upload
} from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  
  // Force light mode on landing page
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);
  
  const [acknowledgements, setAcknowledgements] = useState({
    understand_dv_protection: false,
    understand_not_repairs: false,
    understand_optional: false,
    understand_policy_terms: false,
    understand_no_guarantee: false,
    understand_age_requirement: false,
    understand_driver_eligibility: false,
  });

  const allAcknowledged = Object.values(acknowledgements).every(Boolean);
  const totalAcknowledgements = Object.keys(acknowledgements).length;
  const completedCount = Object.values(acknowledgements).filter(Boolean).length;

  const handleAcknowledgementChange = (key, checked) => {
    setAcknowledgements(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleProceed = () => {
    if (allAcknowledged) {
      navigate('/quote');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-[#F1F5F9]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="https://losspay.com" className="flex items-center">
              <img 
                src="https://images.losspay.com/losspay/losspay-logo.png" 
                alt="LossPay" 
                className="h-[28px] sm:h-[36px] w-auto"
                data-testid="losspay-logo"
              />
            </a>
            <span className="text-sm text-[#5B6B7C] hidden sm:block font-medium">Diminished Value Protection</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Page Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white text-[#2F5BFF] text-sm font-medium px-5 py-2.5 rounded-full mb-5 shadow-sm border border-blue-100">
            <FileText className="h-4 w-4" />
            Important Disclosure Information
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0B1F33] mb-4 tracking-tight">
            Before You Get Your Quote
          </h1>
          <p className="text-lg text-[#5B6B7C] max-w-2xl mx-auto leading-relaxed">
            Please review the following information about LossPay Diminished Value Protection and acknowledge your understanding before proceeding.
          </p>
        </div>

        {/* WHAT YOU'LL NEED SECTION - Amber/Yellow Highlight */}
        <section className="mb-8" data-testid="section-what-you-need">
          <div className="bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7] rounded-2xl p-6 sm:p-8 shadow-sm border border-[#F59E0B]/25 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#F59E0B]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="flex flex-col sm:flex-row items-start gap-5 relative">
              <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-2xl flex items-center justify-center shadow-lg shadow-amber-200">
                <FileSearch className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F33] mb-3">
                  What You'll Need to Get a Quote
                </h2>
                <p className="text-[#5B6B7C] mb-6 leading-relaxed">
                  To provide an accurate quote, we'll need your <strong className="text-[#92400E]">Vehicle Identification Number (VIN)</strong> — a unique 17-character code that identifies your vehicle.
                </p>
                
                {/* VIN Input Methods */}
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white/90 rounded-xl p-5 border border-[#F59E0B]/20 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-xl flex items-center justify-center">
                        <Keyboard className="h-5 w-5 text-[#B45309]" />
                      </div>
                      <h3 className="font-semibold text-[#0B1F33]">Type It In</h3>
                    </div>
                    <p className="text-sm text-[#5B6B7C] leading-relaxed">
                      Manually enter your 17-character VIN using your keyboard
                    </p>
                  </div>
                  <div className="bg-white/90 rounded-xl p-5 border border-[#F59E0B]/20 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-xl flex items-center justify-center">
                        <Camera className="h-5 w-5 text-[#B45309]" />
                      </div>
                      <h3 className="font-semibold text-[#0B1F33]">Scan with Camera</h3>
                    </div>
                    <p className="text-sm text-[#5B6B7C] leading-relaxed">
                      Use your device's camera to scan and auto-detect the VIN
                    </p>
                  </div>
                  <div className="bg-white/90 rounded-xl p-5 border border-[#F59E0B]/20 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-xl flex items-center justify-center">
                        <Upload className="h-5 w-5 text-[#B45309]" />
                      </div>
                      <h3 className="font-semibold text-[#0B1F33]">Upload Photo</h3>
                    </div>
                    <p className="text-sm text-[#5B6B7C] leading-relaxed">
                      Upload a photo of your VIN sticker or registration document
                    </p>
                  </div>
                </div>

                {/* Where to Find VIN */}
                <div className="bg-white/80 rounded-xl p-4 border border-[#F59E0B]/15">
                  <p className="text-sm text-[#5B6B7C] flex items-start gap-3">
                    <Info className="h-4 w-4 text-[#D97706] mt-0.5 flex-shrink-0" />
                    <span>
                      <strong className="text-[#0B1F33]">Where to find your VIN:</strong> Driver's side dashboard (visible through windshield), driver's side door jamb sticker, vehicle registration, or insurance card.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Driver Eligibility Notice Section */}
        <section className="mb-8" data-testid="section-driver-eligibility">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 overflow-hidden relative p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-200">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-amber-900 mb-3">
                  Driver Eligibility Requirements
                </h2>
                <p className="text-amber-800 leading-relaxed mb-4">
                  To qualify for LossPay Diminished Value Protection, drivers must meet certain eligibility criteria. Coverage is <strong>not available</strong> to drivers with any of the following within the past <strong>3 years</strong>:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-3 text-amber-800">
                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <span>DUI/DWI convictions</span>
                  </li>
                  <li className="flex items-center gap-3 text-amber-800">
                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <span>License suspensions</span>
                  </li>
                  <li className="flex items-center gap-3 text-amber-800">
                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <span>Reckless driving citations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 1: Product Explanation - Blue Gradient */}
        <section className="mb-8" data-testid="section-product-explanation">
          <div className="bg-gradient-to-br from-[#EFF6FF] to-[#F8FAFC] rounded-2xl shadow-sm border border-blue-100 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#2F5BFF]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="flex">
              {/* Left Blue Accent Border */}
              <div className="w-1.5 bg-gradient-to-b from-[#2F5BFF] to-[#6366F1] flex-shrink-0"></div>
              <div className="flex-1 p-6 sm:p-8 relative">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2F5BFF] to-[#4F46E5] rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F33]">
                    Protect Your Vehicle's Resale Value
                  </h2>
                </div>
                <p className="text-[#5B6B7C] leading-relaxed mb-4">
                  <strong className="text-[#1E40AF]">LossPay Diminished Value Protection</strong> is designed to help protect your vehicle's equity by covering potential resale value loss after a qualifying repair event.
                </p>
                <p className="text-[#5B6B7C] leading-relaxed mb-6">
                  Even when repairs are completed to a high standard, vehicles with an accident history may sell for less in the market. This reduction in value is commonly referred to as <strong className="text-[#1E40AF]">diminished value</strong>.
                </p>
                
                {/* Callout Card - Enhanced */}
                <div className="bg-white rounded-xl p-5 border border-blue-100 shadow-sm">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#2F5BFF] to-[#4F46E5] rounded-lg flex items-center justify-center shadow-md">
                        <Info className="h-5 w-5 text-white" />
                      </div>
                    </div>
                    <div>
                      <p className="text-[#1E40AF] font-semibold mb-1">Key Distinction</p>
                      <p className="text-[#5B6B7C] text-sm leading-relaxed">
                        Standard auto insurance typically covers repairs.<br />
                        <strong className="text-[#0B1F33]">LossPay is designed to help protect the value that may be lost after those repairs.</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: Coverage Overview - Teal/Green Gradient */}
        <section className="mb-8" data-testid="section-coverage-overview">
          <div className="bg-gradient-to-br from-[#F0FDFA] to-[#F8FAFC] rounded-2xl shadow-sm border border-teal-100 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#0D9488]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="flex">
              <div className="w-1.5 bg-gradient-to-b from-[#0D9488] to-[#14B8A6] flex-shrink-0"></div>
              <div className="flex-1 p-6 sm:p-8 relative">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] rounded-xl flex items-center justify-center shadow-lg shadow-teal-200">
                    <CheckCircle2 className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F33]">
                    What This Coverage Is Designed To Protect
                  </h2>
                </div>
                <p className="text-[#5B6B7C] leading-relaxed mb-4">
                  LossPay provides <strong className="text-[#0F766E]">financial protection for diminished resale value</strong> that may occur after a qualifying accident where a vehicle is repaired and returned to service.
                </p>
                <p className="text-[#5B6B7C] leading-relaxed">
                  If a covered repair event results in a measurable reduction in market value, LossPay may compensate the policyholder for the difference between the vehicle's pre-accident market value and post-repair market value, <strong className="text-[#0B1F33]">subject to policy limits, eligibility requirements, and conditions</strong>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3: Coverage Clarification - Yellow border highlight */}
        <section className="mb-8" data-testid="section-coverage-clarification">
          <div className="bg-white rounded-2xl shadow-sm border-2 border-[#F59E0B]/40 p-6 sm:p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-xl flex items-center justify-center">
                <CircleMinus className="h-6 w-6 text-[#B45309]" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F33]">
                What LossPay Does Not Cover
              </h2>
            </div>
            
            <p className="text-[#5B6B7C] mb-6">LossPay does not provide coverage for:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { icon: Wrench, text: "Vehicle repair costs" },
                { icon: Car, text: "Collision or comprehensive damage" },
                { icon: Heart, text: "Bodily injury or liability claims" },
                { icon: Settings, text: "Mechanical breakdowns" },
                { icon: FileWarning, text: "Vehicle warranties or service contracts" },
                { icon: Receipt, text: "Loan payoff protection (GAP insurance)" },
                { icon: AlertTriangle, text: "Total loss events" },
                { icon: TrendingDown, text: "Normal depreciation from age, mileage, or market conditions" },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 bg-[#FFFBEB] rounded-xl border border-[#F59E0B]/20 hover:border-[#F59E0B]/40 transition-colors"
                >
                  <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border border-[#F59E0B]/20">
                    <item.icon className="h-4 w-4 text-[#B45309]" />
                  </div>
                  <span className="text-[#5B6B7C] text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4: Optional Product Disclosure */}
        <section className="mb-8" data-testid="section-optional-disclosure">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-12 h-12 bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-xl flex items-center justify-center">
                <Info className="h-6 w-6 text-[#B45309]" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F33]">
                Optional Coverage Notice
              </h2>
            </div>
            <div className="space-y-3 text-[#5B6B7C] leading-relaxed">
              <p className="font-semibold text-[#0B1F33]">
                LossPay coverage is completely optional.
              </p>
              <p>
                Purchasing this protection is not required in order to obtain vehicle financing, insurance, leasing, or dealership services.
              </p>
              <p>
                Declining this coverage will not affect eligibility for any other product or service.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 5: Claims Process Disclosure */}
        <section className="mb-8" data-testid="section-claims-process">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-12 h-12 bg-gradient-to-br from-[#EDE9FE] to-[#DDD6FE] rounded-xl flex items-center justify-center">
                <ClipboardCheck className="h-6 w-6 text-[#7C3AED]" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F33]">
                How Claims Are Evaluated
              </h2>
            </div>
            <div className="space-y-4 text-[#5B6B7C] leading-relaxed">
              <p>
                Claims are evaluated using vehicle-specific data including VIN information, repair documentation, and market valuation analysis.
              </p>
              <p>
                Payment of any claim depends on verification of a qualifying repair event and compliance with the terms, conditions, limits, and exclusions stated in the policy.
              </p>
              <p className="font-semibold text-[#0B1F33]">
                Not all accidents result in diminished resale value, and not all claims will result in payment.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 6: Regulatory Disclosure */}
        <section className="mb-8" data-testid="section-regulatory-disclosure">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-12 h-12 bg-[#F1F5F9] rounded-xl flex items-center justify-center border border-slate-200">
                <Landmark className="h-6 w-6 text-[#64748B]" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F33]">
                Insurance Notice
              </h2>
            </div>
            <div className="space-y-3 text-[#5B6B7C] leading-relaxed">
              <p>
                Insurance products offered through LossPay may be underwritten by licensed insurers including surplus lines carriers.
              </p>
              <p className="font-semibold text-[#0B1F33]">
                Surplus lines insurers are not protected by state insurance guaranty funds.
              </p>
              <p>
                Coverage availability and eligibility may vary by state.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 7: Required User Acknowledgements - Blue accent */}
        <section className="mb-8" data-testid="section-acknowledgements">
          <div className="bg-gradient-to-br from-[#EFF6FF] to-[#F8FAFC] rounded-2xl shadow-sm border-2 border-[#2F5BFF]/30 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#2F5BFF]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="flex">
              <div className="w-1.5 bg-gradient-to-b from-[#2F5BFF] to-[#6366F1] flex-shrink-0"></div>
              <div className="flex-1">
                <div className="bg-gradient-to-r from-[#EEF2FF] to-[#E0E7FF] px-6 sm:px-8 py-5 border-b border-[#2F5BFF]/20">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#2F5BFF] to-[#4F46E5] rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
                      <Scale className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F33]">
                        Required Acknowledgements
                      </h2>
                      <p className="text-sm text-[#5B6B7C]">Please confirm your understanding of each statement</p>
                    </div>
                  </div>
                </div>
            
            <div className="p-6 sm:p-8 space-y-4">
              {/* Acknowledgement 1 */}
              <label 
                className="flex items-start gap-4 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all hover:border-slate-300 bg-white"
              >
                <Checkbox
                  data-testid="ack-dv-protection"
                  checked={acknowledgements.understand_dv_protection}
                  onCheckedChange={(checked) => handleAcknowledgementChange('understand_dv_protection', checked)}
                  className="mt-0.5 data-[state=checked]:bg-white data-[state=checked]:border-slate-400 data-[state=checked]:text-black"
                />
                <span className="text-[#5B6B7C] text-sm leading-relaxed">
                  I understand that LossPay protects against diminished resale value after a qualifying repair event.
                </span>
              </label>

              {/* Acknowledgement 2 */}
              <label 
                className="flex items-start gap-4 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all hover:border-slate-300 bg-white"
              >
                <Checkbox
                  data-testid="ack-not-repairs"
                  checked={acknowledgements.understand_not_repairs}
                  onCheckedChange={(checked) => handleAcknowledgementChange('understand_not_repairs', checked)}
                  className="mt-0.5 data-[state=checked]:bg-white data-[state=checked]:border-slate-400 data-[state=checked]:text-black"
                />
                <span className="text-[#5B6B7C] text-sm leading-relaxed">
                  I understand that LossPay does not cover vehicle repairs, physical damage, liability, or total loss.
                </span>
              </label>

              {/* Acknowledgement 3 */}
              <label 
                className="flex items-start gap-4 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all hover:border-slate-300 bg-white"
              >
                <Checkbox
                  data-testid="ack-optional"
                  checked={acknowledgements.understand_optional}
                  onCheckedChange={(checked) => handleAcknowledgementChange('understand_optional', checked)}
                  className="mt-0.5 data-[state=checked]:bg-white data-[state=checked]:border-slate-400 data-[state=checked]:text-black"
                />
                <span className="text-[#5B6B7C] text-sm leading-relaxed">
                  I understand that LossPay coverage is optional and is not required to obtain financing, insurance, leasing, or dealership services.
                </span>
              </label>

              {/* Acknowledgement 4 */}
              <label 
                className="flex items-start gap-4 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all hover:border-slate-300 bg-white"
              >
                <Checkbox
                  data-testid="ack-policy-terms"
                  checked={acknowledgements.understand_policy_terms}
                  onCheckedChange={(checked) => handleAcknowledgementChange('understand_policy_terms', checked)}
                  className="mt-0.5 data-[state=checked]:bg-white data-[state=checked]:border-slate-400 data-[state=checked]:text-black"
                />
                <span className="text-[#5B6B7C] text-sm leading-relaxed">
                  I understand that claim payments are subject to policy terms, conditions, limits, and exclusions.
                </span>
              </label>

              {/* Acknowledgement 5 */}
              <label 
                className="flex items-start gap-4 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all hover:border-slate-300 bg-white"
              >
                <Checkbox
                  data-testid="ack-no-guarantee"
                  checked={acknowledgements.understand_no_guarantee}
                  onCheckedChange={(checked) => handleAcknowledgementChange('understand_no_guarantee', checked)}
                  className="mt-0.5 data-[state=checked]:bg-white data-[state=checked]:border-slate-400 data-[state=checked]:text-black"
                />
                <span className="text-[#5B6B7C] text-sm leading-relaxed">
                  I understand that not every accident results in diminished resale value and that some claims may not result in payment.
                </span>
              </label>

              {/* Acknowledgement 6 - Age Requirement */}
              <label 
                className="flex items-start gap-4 p-4 rounded-xl border-2 border-slate-200 cursor-pointer transition-all hover:border-slate-300 bg-white"
              >
                <Checkbox
                  data-testid="ack-age-requirement"
                  checked={acknowledgements.understand_age_requirement}
                  onCheckedChange={(checked) => handleAcknowledgementChange('understand_age_requirement', checked)}
                  className="mt-0.5 data-[state=checked]:bg-white data-[state=checked]:border-slate-400 data-[state=checked]:text-black"
                />
                <span className="text-[#5B6B7C] text-sm leading-relaxed">
                  I understand that LossPay coverage is available only to applicants who are <strong className="text-[#0B1F33]">30 years of age or older</strong>.
                </span>
              </label>

              {/* Driver Eligibility Acknowledgement */}
              <label className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 cursor-pointer hover:border-amber-300 transition-all" data-testid="ack-driver-eligibility-container">
                <Checkbox 
                  id="ack-driver-eligibility"
                  data-testid="ack-driver-eligibility"
                  checked={acknowledgements.understand_driver_eligibility}
                  onCheckedChange={(checked) => handleAcknowledgementChange('understand_driver_eligibility', checked)}
                  className="mt-0.5 data-[state=checked]:bg-white data-[state=checked]:border-slate-400 data-[state=checked]:text-black"
                />
                <span className="text-[#5B6B7C] text-sm leading-relaxed">
                  I understand that coverage is <strong className="text-red-600">not available</strong> to drivers with <strong className="text-[#0B1F33]">DUI/DWI convictions, license suspensions, or reckless driving citations</strong> within the past 3 years.
                </span>
              </label>

              {/* Progress indicator - Enhanced */}
              <div className="pt-6 border-t border-[#2F5BFF]/10">
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-[#5B6B7C]">Acknowledgements completed</span>
                  <span className={`font-semibold ${allAcknowledged ? 'text-[#2F5BFF]' : 'text-[#2F5BFF]'}`}>
                    {completedCount} of {totalAcknowledgements}
                  </span>
                </div>
                <div className="w-full bg-[#E0E7FF] rounded-full h-2.5 overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      allAcknowledged 
                        ? 'bg-gradient-to-r from-[#2F5BFF] to-[#4F46E5]' 
                        : 'bg-gradient-to-r from-[#2F5BFF] to-[#6366F1]'
                    }`}
                    style={{ width: `${(completedCount / totalAcknowledgements) * 100}%` }}
                  />
                </div>
              </div>
            </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: Proceed to Quote - Enhanced CTA */}
        <section className="mb-10" data-testid="section-proceed">
          <div className={`rounded-2xl p-6 sm:p-8 text-center transition-all duration-300 ${
            allAcknowledged 
              ? 'bg-white border-2 border-[#2F5BFF]/30 shadow-lg shadow-blue-100' 
              : 'bg-white border border-slate-200 shadow-sm'
          }`}>
            {allAcknowledged && (
              <div className="mb-4">
                <span className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2F5BFF] to-[#4F46E5] text-white text-sm font-semibold px-4 py-2 rounded-full shadow-md">
                  <Sparkles className="h-4 w-4" />
                  Ready to Continue
                </span>
              </div>
            )}
            <Button
              data-testid="proceed-to-quote-btn"
              size="lg"
              onClick={handleProceed}
              disabled={!allAcknowledged}
              className={`w-full px-6 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-semibold transition-all h-auto rounded-xl ${
                allAcknowledged 
                  ? 'bg-gradient-to-r from-[#2F5BFF] to-[#4F46E5] hover:from-[#1E40AF] hover:to-[#2F5BFF] text-white shadow-lg hover:shadow-xl' 
                  : 'bg-slate-100 text-[#94A3B8] cursor-not-allowed'
              }`}
            >
              {allAcknowledged ? (
                <span className="flex items-center justify-center">
                  See My Coverage Options
                  <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
                </span>
              ) : (
                <span className="block text-center">
                  Review and confirm to continue
                </span>
              )}
            </Button>
            
            {!allAcknowledged && (
              <p className="text-sm text-[#94A3B8] mt-5">
                Please review and acknowledge all {totalAcknowledgements - completedCount} remaining statements to proceed.
              </p>
            )}
          </div>
        </section>

        {/* Regulatory Footer */}
        <footer className="border-t border-slate-200 pt-10 pb-6">
          {/* Logo and Copyright */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-[#5B6B7C] mb-10">
            <div className="flex items-center">
              <img 
                src="https://images.losspay.com/losspay/losspay-logo.png" 
                alt="LossPay" 
                className="h-6 sm:h-8 w-auto opacity-70"
              />
            </div>
            <p>&copy; {new Date().getFullYear()} LossPay Risk Technologies, Inc. All rights reserved.</p>
          </div>

          {/* Regulatory Disclosures */}
          <div className="border-t border-slate-200 pt-10 space-y-6 text-xs text-[#5B6B7C] leading-relaxed">
            <div>
              <h4 className="font-semibold text-[#0B1F33] mb-1">Corporate and Licensing Disclosure</h4>
              <p>LossPay Risk Technologies, Inc., doing business as LossPay Insurance Agency and LossPay, operates as a licensed insurance agency and managing general agency that arranges insurance coverage with duly licensed insurers.</p>
            </div>

            <div>
              <h4 className="font-semibold text-[#0B1F33] mb-1">Product Description Disclaimer</h4>
              <p>LossPay provides Diminished Value Protection coverage designed to help protect against potential resale value loss after a qualifying vehicle repair event. LossPay coverage does not replace or duplicate automobile insurance and does not provide coverage for vehicle repairs, liability, or physical damage.</p>
            </div>

            <div>
              <h4 className="font-semibold text-[#0B1F33] mb-1">Coverage Disclaimer</h4>
              <p>All descriptions of coverage on this website are provided for general informational purposes only. Coverage is subject to the specific terms, conditions, limits, deductibles, and exclusions contained in the issued policy and declarations page.</p>
            </div>

            <div>
              <h4 className="font-semibold text-[#0B1F33] mb-1">Surplus Lines Regulatory Notice</h4>
              <p>Insurance products offered through LossPay Insurance Agency may be underwritten by surplus lines insurers. Surplus lines insurers are not protected by state insurance guaranty funds.</p>
            </div>

            <div>
              <h4 className="font-semibold text-[#0B1F33] mb-1">Jurisdiction and Availability Notice</h4>
              <p>Insurance products and services described on this website are offered only where permitted by law. Coverage availability and eligibility may vary by state and may not be available in all jurisdictions.</p>
            </div>

            <div>
              <h4 className="font-semibold text-[#0B1F33] mb-1">Optional Coverage Notice</h4>
              <p>Purchase of LossPay coverage is optional. LossPay coverage is not required in order to obtain vehicle financing, insurance, leasing, or dealership services.</p>
            </div>

            <div>
              <h4 className="font-semibold text-[#0B1F33] mb-1">Consumer Disclosure Reminder</h4>
              <p>Not all accidents result in diminished resale value. Payment of any claim is subject to verification of a qualifying repair event and compliance with the terms and conditions of the policy.</p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 pt-6 border-t border-slate-200">
              <a 
                href="https://losspay.com/StateNotices"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#3B5BDB] hover:text-[#2F4BC7] transition-colors"
              >
                State Notices
              </a>
              <a 
                href="https://losspay.com/PrivacyPolicy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#3B5BDB] hover:text-[#2F4BC7] transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="https://losspay.com/TermsOfUse"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#3B5BDB] hover:text-[#2F4BC7] transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
